import { inject } from '@angular/core';
import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { catchError, switchMap, take, throwError } from 'rxjs';
import { environment } from '../../../environments/environment';
import { PmOneLoginTokenService } from '../../features/auth/pm-onelogin-token.service';

/**
 * OneLogin/APIM pilot bearer interceptor — attaches the in-memory OneLogin access_token (never
 * localStorage, see PmOneLoginTokenService) to requests bound for pm-api, then refreshes and retries
 * once on 401.
 *
 * Targets `pmApiApimBaseUrl` when Platform confirms PM5's APIM route, and — per the PDF's own
 * "Local development notes" ("Without APIM: PM5 API may accept OneLogin bearer... only in local
 * config") — also targets `pmApiBaseUrl` directly so local dev works before APIM exists. This is
 * the only bearer-attaching interceptor PM5 registers — OneLogin is the sole login mechanism.
 */
export const pmOneLoginBearerInterceptor: HttpInterceptorFn = (req, next) => {
  const apimBase = (environment.pmApiApimBaseUrl ?? '').trim();
  const directBase = (environment.pmApiBaseUrl ?? '').trim();
  const targetsPmApi =
    (apimBase.length > 0 && req.url.startsWith(apimBase)) ||
    (directBase.length > 0 && req.url.startsWith(directBase)) ||
    req.url.startsWith('/api/');
  if (!targetsPmApi || req.headers.has('Authorization')) {
    return next(req);
  }

  const tokenService = inject(PmOneLoginTokenService);
  const oidcSecurityService = inject(OidcSecurityService);

  const token = tokenService.accessToken();
  const authorized = token ? req.clone({ setHeaders: { Authorization: `Bearer ${token}` } }) : req;

  return next(authorized).pipe(
    catchError((error: unknown) => {
      if (!(error instanceof HttpErrorResponse) || error.status !== 401 || !token) {
        return throwError(() => error);
      }

      return oidcSecurityService.forceRefreshSession().pipe(
        take(1),
        switchMap((refreshed) => {
          const refreshedToken =
            refreshed?.accessToken || (refreshed as { access_token?: string })?.access_token;
          if (!refreshedToken) {
            tokenService.clear();
            return throwError(() => error);
          }
          tokenService.setAccessToken(refreshedToken);
          return next(req.clone({ setHeaders: { Authorization: `Bearer ${refreshedToken}` } }));
        }),
        catchError((refreshError) => {
          tokenService.clear();
          return throwError(() => refreshError);
        })
      );
    })
  );
};
